/**
 * Bala Vivecana — full classical Ṣaḍbala + Bhāva-bala + Iṣṭa/Kaṣṭa-phala.
 *
 * Six-component planetary strength with all classical sub-components:
 *   • Sthāna-bala (5 sub-components)
 *   • Dig-bala
 *   • Kāla-bala (10 sub-components)
 *   • Ceṣṭā-bala
 *   • Naisargika-bala
 *   • Dṛk-bala
 * Plus 12-house Bhāva-bala (3 components) and Iṣṭa/Kaṣṭa-phala per planet.
 *
 * Visualisation: radar (summary), stacked bar (component breakdown),
 * heatmap (Bhāva), diverging bar (Iṣṭa/Kaṣṭa).
 */
import { useCallback, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError, api } from '@/api/client';
import type { ChartCreateRequest, ShadbalaFullResponse } from '@/api/types';
import {
  BhavaHeatmap,
  IshtaKashtaBar,
  ShadbalaRadar,
  ShadbalaStacked,
} from '@/components/BalaCharts';
import { ChartInputForm } from '@/components/ChartInputForm';
import { useLang } from '@/i18n/useLang';

export function BalaPage() {
  const { lang, t } = useLang();
  const [report, setReport] = useState<ShadbalaFullResponse | null>(null);

  const balaMutation = useMutation<ShadbalaFullResponse, ApiError, ChartCreateRequest>({
    mutationFn: (req) => api.bala(req),
    onSuccess: setReport,
  });

  const handleSubmit = useCallback(
    (req: ChartCreateRequest) => {
      setReport(null);
      balaMutation.mutate(req);
    },
    [balaMutation],
  );

  return (
    <article className="mx-auto max-w-5xl space-y-6 px-4 py-8 text-stone-100">
      <header>
        <h1 className="text-2xl font-bold text-saffron-200">
          {t('बल विवेचना — षड्बल, भाव-बल एवं इष्ट/कष्ट-फल', 'Bala Vivecana — Ṣaḍbala, Bhāva-bala & Iṣṭa/Kaṣṭa')}
        </h1>
        <p className="mt-1 text-sm text-stone-400">
          {t(
            'सम्पूर्ण शास्त्रीय गणना — 6 स्रोत, सभी उप-घटक, 12 भावों का बल, एवं प्रत्येक ग्रह का इष्ट / कष्ट फल।',
            'Full classical computation — 6 sources, all sub-components, 12-house bhāva-bala, plus per-planet Iṣṭa / Kaṣṭa.',
          )}
        </p>
      </header>

      <ChartInputForm onSubmit={handleSubmit} isSubmitting={balaMutation.isPending} />

      {balaMutation.isError && (
        <div className="rounded-2xl bg-red-950/40 p-4 ring-1 ring-red-800">
          <p className="text-sm text-red-200">
            {balaMutation.error?.detail ?? t('त्रुटि', 'Error')}
          </p>
        </div>
      )}

      {balaMutation.isPending && (
        <p className="text-sm italic text-stone-500">{t('गणना हो रही है…', 'Computing…')}</p>
      )}

      {report && <BalaReport report={report} lang={lang} />}
    </article>
  );
}

function BalaReport({ report, lang }: { report: ShadbalaFullResponse; lang: 'hi' | 'en' }) {
  const t = (hi: string, en: string) => (lang === 'hi' ? hi : en);

  return (
    <div className="space-y-6">
      {/* Radar */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('षड्बल सारांश — रडार चार्ट', 'Ṣaḍbala Summary — Radar')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'भरा बहुभुज वास्तविक बल; डैश्ड बहुभुज शास्त्रीय न्यूनतम-स्तर। हरा बिन्दु = पर्याप्त; लाल = अपर्याप्त।',
            'Filled polygon shows actual Ṣaḍbala; dashed marks the classical minimum. Green dot = sufficient; red = below floor.',
          )}
        </p>
        <div className="flex justify-center">
          <ShadbalaRadar rows={report.rows} lang={lang} size={460} />
        </div>
      </section>

      {/* Stacked bar */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('घटक-वार विश्लेषण', 'Component Breakdown')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'प्रत्येक ग्रह के 6 बल-घटक स्तरीकृत; लाल डैश्ड रेखा शास्त्रीय न्यूनतम है।',
            'Six Ṣaḍbala components stacked per planet; red dashed line marks the classical minimum.',
          )}
        </p>
        <ShadbalaStacked rows={report.rows} lang={lang} width={600} height={320} />
      </section>

      {/* Detailed table */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('विस्तृत सारिणी (विरूप)', 'Detailed Table (Virūpas)')}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-800 text-xs uppercase tracking-wider text-stone-500">
                <th className="py-1.5 text-left font-semibold">{t('ग्रह', 'Planet')}</th>
                <th className="text-right">{t('स्थान', 'Sthāna')}</th>
                <th className="text-right">{t('दिक्', 'Dig')}</th>
                <th className="text-right">{t('काल', 'Kāla')}</th>
                <th className="text-right">{t('चेष्टा', 'Ceṣṭā')}</th>
                <th className="text-right">{t('नैसर्गिक', 'Nais.')}</th>
                <th className="text-right">{t('दृक्', 'Dṛk')}</th>
                <th className="text-right">{t('कुल', 'Total')}</th>
                <th className="text-right">{t('रूप', 'Rūpa')}</th>
                <th className="text-right">{t('न्यूनतम', 'Min')}</th>
                <th className="text-right">?</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.planet_id} className="border-b border-stone-900 hover:bg-stone-900/40">
                  <td className="py-1 font-semibold">{lang === 'hi' ? r.planet_hi : r.planet_en}</td>
                  <td className="text-right tabular-nums">{r.sthana.total.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.dig.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.total.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.cheshta.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.naisargika.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.drik.toFixed(1)}</td>
                  <td className="text-right font-bold tabular-nums">{r.total_virupas.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.total_rupas.toFixed(2)}</td>
                  <td className="text-right tabular-nums text-stone-500">{r.required_rupas.toFixed(1)}</td>
                  <td className="text-right">
                    {r.sufficient ? (
                      <span className="rounded bg-green-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-green-300">
                        ✓
                      </span>
                    ) : (
                      <span className="rounded bg-red-900/40 px-1.5 py-0.5 text-[10px] font-semibold text-red-300">
                        ✗
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Sthāna-bala expansion */}
      <details className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <summary className="cursor-pointer text-base font-semibold text-saffron-200">
          {t('स्थान-बल — उप-घटक', 'Sthāna-bala — Sub-components')}
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-stone-800 text-xs uppercase tracking-wider text-stone-500">
                <th className="text-left">{t('ग्रह', 'Planet')}</th>
                <th className="text-right">{t('उच्च', 'Ucca')}</th>
                <th className="text-right">{t('सप्तवर्गज', 'Saptavargaja')}</th>
                <th className="text-right">{t('ओजयुग्म', 'Oja-yugma')}</th>
                <th className="text-right">{t('केन्द्र', 'Kendra')}</th>
                <th className="text-right">{t('द्रेष्काण', 'Drekkāṇa')}</th>
                <th className="text-right">{t('कुल', 'Total')}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.planet_id} className="border-b border-stone-900">
                  <td className="py-1 font-semibold">{lang === 'hi' ? r.planet_hi : r.planet_en}</td>
                  <td className="text-right tabular-nums">{r.sthana.ucca.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.sthana.saptavargaja.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.sthana.oja_yugma.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.sthana.kendra.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.sthana.drekkana.toFixed(1)}</td>
                  <td className="text-right font-bold tabular-nums">{r.sthana.total.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* Kāla-bala expansion */}
      <details className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <summary className="cursor-pointer text-base font-semibold text-saffron-200">
          {t('काल-बल — उप-घटक (10)', 'Kāla-bala — Sub-components (10)')}
        </summary>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-stone-800 uppercase tracking-wider text-stone-500">
                <th className="text-left">{t('ग्रह', 'Planet')}</th>
                <th className="text-right">{t('नतो', 'Nato')}</th>
                <th className="text-right">{t('पक्ष', 'Pakṣa')}</th>
                <th className="text-right">{t('त्रिभाग', 'Tribh.')}</th>
                <th className="text-right">{t('अब्द', 'Abda')}</th>
                <th className="text-right">{t('मास', 'Māsa')}</th>
                <th className="text-right">{t('वार', 'Vāra')}</th>
                <th className="text-right">{t('होरा', 'Horā')}</th>
                <th className="text-right">{t('अयन', 'Ayana')}</th>
                <th className="text-right">{t('युद्ध', 'Yuddha')}</th>
                <th className="text-right">{t('कुल', 'Total')}</th>
              </tr>
            </thead>
            <tbody>
              {report.rows.map((r) => (
                <tr key={r.planet_id} className="border-b border-stone-900">
                  <td className="py-1 font-semibold">{lang === 'hi' ? r.planet_hi : r.planet_en}</td>
                  <td className="text-right tabular-nums">{r.kala.nato_nnata.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.paksha.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.tribhaga.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.abda.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.masa.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.vara.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.hora.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.ayana.toFixed(1)}</td>
                  <td className="text-right tabular-nums">{r.kala.yuddha.toFixed(1)}</td>
                  <td className="text-right font-bold tabular-nums">{r.kala.total.toFixed(1)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {/* Bhāva heatmap */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('भाव-बल विश्लेषण', 'Bhāva-bala Analysis')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'बारह भावों का बल — भावाधिपति, भाव-दिग्बल एवं भाव-दृष्टिबल। प्रबल भाव गहन रंग में।',
            'Twelve-house strength: bhāvādhipati, bhāva-dig, bhāva-dṛṣṭi. Stronger houses appear deeper.',
          )}
        </p>
        <BhavaHeatmap rows={report.bhava} lang={lang} width={640} height={200} />
      </section>

      {/* Iṣṭa/Kaṣṭa */}
      <section className="rounded-2xl bg-stone-950/50 p-5 ring-1 ring-stone-800">
        <h2 className="mb-3 text-base font-semibold text-saffron-200">
          {t('इष्ट / कष्ट-फल', 'Iṣṭa / Kaṣṭa-phala')}
        </h2>
        <p className="mb-3 text-xs text-stone-500">
          {t(
            'फलदीपिका 2.20 — इष्ट = √(उच्च-बल × चेष्टा-बल); कष्ट = 60 − इष्ट।',
            'Phaladīpikā 2.20 — Iṣṭa = √(Ucca-bala × Ceṣṭā-bala); Kaṣṭa = 60 − Iṣṭa.',
          )}
        </p>
        <IshtaKashtaBar rows={report.rows} lang={lang} width={540} height={320} />
      </section>

      {/* Methodology */}
      <section className="rounded-2xl bg-stone-900/30 p-3 ring-1 ring-stone-800/50">
        <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-stone-500">
          {t('विधि-सूचना', 'Methodology')}
        </h3>
        <ul className="space-y-1 text-[10px] leading-snug text-stone-500">
          {report.methodology.map((m, i) => (
            <li key={i}>
              <span className="text-stone-400">{lang === 'hi' ? m.method_hi : m.method_en}</span>
              {' — '}
              <em>{lang === 'hi' ? m.source_hi : m.source_en}</em>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-[9px] text-stone-700">{report.rules_version}</p>
      </section>
    </div>
  );
}
